"use client";

import React, { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/platform/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  WORKSPACE_SEARCHABLE_SELECT_CLEAR_ITEM_CLASS,
  WORKSPACE_SEARCHABLE_SELECT_ITEM_CLASS,
  WORKSPACE_SEARCHABLE_SELECT_LAYOUT_CLASS,
  WORKSPACE_SEARCHABLE_SELECT_PANEL_CLASS,
  WORKSPACE_SEARCHABLE_SELECT_TRIGGER_CLASS,
} from "@/lib/constants/workspace-styles";
import type { OptionItem } from "@/components/workspace/tasks-shared";
import { useTranslation } from "@/lib/hooks/use-translation";

function splitOptionHintLines(hint?: string): string[] {
  const raw = String(hint ?? "").trim();
  if (!raw) return [];
  if (!raw.includes("|")) return [raw];
  const parts = raw
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return parts;
  return [parts[0], parts.slice(1).join(" | ")];
}

function optionMatchesQuery(option: OptionItem, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = `${option.value} ${option.label} ${option.hint ?? ""}`.toLowerCase();
  return haystack.includes(needle);
}

function OptionLogo({ imageUrl, label }: { imageUrl?: string; label: string }) {
  const src = String(imageUrl ?? "").trim();
  if (!src.startsWith("https://")) return null;
  return (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-1 ring-1 ring-black/10"
      title={label}
    >
      <img
        src={src}
        alt=""
        aria-hidden
        className="h-full w-full object-contain"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </span>
  );
}

function OptionItemContent({ option }: { option: OptionItem }) {
  const label = option.label.trim();
  const hintLines = splitOptionHintLines(option.hint).filter(
    (line) => line.length > 0 && line.toLowerCase() !== label.toLowerCase(),
  );
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <OptionLogo imageUrl={option.imageUrl} label={option.label} />
      <div className="flex min-w-0 flex-col gap-0.5 py-0.5">
        <span className="leading-tight">{option.label}</span>
        {hintLines.map((line, index) => (
          <span key={`${index}-${line}`} className="text-[11px] leading-snug text-muted-foreground/95 break-words [overflow-wrap:anywhere]">
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}

function closePopoverAfterSelect(
  apply: () => void,
  reset: () => void,
): void {
  apply();
  window.requestAnimationFrame(() => {
    reset();
  });
}

export type SearchableSelectLayout = keyof typeof WORKSPACE_SEARCHABLE_SELECT_LAYOUT_CLASS;

/** Sole FE select SoT — SELECT_BASE trigger; do not add native `<select>` or ui/select consumers. */
export type SearchableSelectProps = {
  value: string;
  onChange: (next: string) => void;
  options: OptionItem[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  valueLabelOverride?: string;
  showRawValue?: boolean;
  onOpenChange?: (open: boolean) => void;
  onQueryChange?: (query: string) => void;
  title?: string;
  id?: string;
  ariaLabel?: string;
  testId?: string;
  className?: string;
  layout?: SearchableSelectLayout;
  allowClear?: boolean;
  showSelectedSubline?: boolean;
  disableClientFilter?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
};

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
  loading = false,
  loadingMessage,
  valueLabelOverride,
  showRawValue = true,
  onOpenChange,
  onQueryChange,
  title,
  id,
  ariaLabel,
  testId,
  className,
  layout = "filter",
  allowClear = true,
  showSelectedSubline = false,
  disableClientFilter = false,
  hasMore = false,
  onLoadMore,
  loadMoreLabel,
}) => {
  const { t } = useTranslation();
  const resolvedLoadingMessage = loadingMessage ?? t("common.searchableSelect.loading");
  const resolvedLoadMoreLabel = loadMoreLabel ?? t("common.searchableSelect.loadMore");
  const clearSearchLabel = t("common.searchableSelect.clearSearch");
  const resolvedSearchPlaceholder = (searchPlaceholder ?? "").trim() || t("common.searchableSelect.search");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const normalizedValue = String(value ?? "");
  const visibleOptions =
    disableClientFilter || loading ? options : options.filter((option) => optionMatchesQuery(option, query));
  const queryNeedle = query.trim().toLowerCase();
  const showClear =
    allowClear &&
    !loading &&
    (!queryNeedle || placeholder.toLowerCase().includes(queryNeedle));

  const resetPopoverQuery = () => {
    setQuery("");
    onQueryChange?.("");
  };

  const closePopover = () => {
    setOpen(false);
    resetPopoverQuery();
  };

  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = 0;
    const frame = requestAnimationFrame(() => {
      list.scrollTop = 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [open, query]);

  const selected = options.find((option) => String(option.value) === normalizedValue) ?? null;
  const placeholderNorm = placeholder.trim().toLowerCase();
  const hasClearTwinOption = options.some((option) => {
    const optionValue = String(option.value ?? "").trim().toLowerCase();
    const optionLabel = option.label.trim().toLowerCase();
    if (optionValue === "" || optionValue === "all" || optionValue === "*") return true;
    return placeholderNorm.length > 0 && optionLabel === placeholderNorm;
  });
  const showClearRow = showClear && !hasClearTwinOption;
  const overrideLabel = (valueLabelOverride || "").trim();
  const rawLabel = showRawValue && normalizedValue.trim() ? normalizedValue : "";
  let displayLabel = placeholder;
  if (rawLabel.length > 0) displayLabel = rawLabel;
  if (overrideLabel.length > 0) displayLabel = overrideLabel;
  if (selected?.label) displayLabel = selected.label;
  if (loading) displayLabel = loadingMessage;
  const showPlaceholderTone = !selected && overrideLabel.length === 0 && rawLabel.length === 0;
  const selectedHintLines =
    showSelectedSubline && selected?.hint
      ? splitOptionHintLines(selected.hint).filter(
          (line) => line.length > 0 && line.toLowerCase() !== displayLabel.trim().toLowerCase(),
        )
      : [];
  const rootClassName =
    layout === "form"
      ? "relative w-full min-w-0"
      : layout === "toolbar"
        ? "relative inline-flex w-fit max-w-full shrink-0 max-md:w-full"
        : layout === "pageSize"
          ? "relative inline-flex w-fit max-w-full shrink-0"
          : "relative inline-flex max-w-full min-w-0 w-fit";

  return (
    <div className={rootClassName}>
      <Popover
        modal={false}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            resetPopoverQuery();
          }
          onOpenChange?.(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            disabled={disabled || loading}
            title={title}
            id={id}
            data-testid={testId}
            className={cn(
              WORKSPACE_SEARCHABLE_SELECT_TRIGGER_CLASS,
              WORKSPACE_SEARCHABLE_SELECT_LAYOUT_CLASS[layout],
              selectedHintLines.length > 0 ? "!h-auto min-h-9 py-1.5" : "",
              showPlaceholderTone && "text-muted-foreground/80",
              className,
            )}
          >
            {selectedHintLines.length > 0 && selected !== null ? (
              <span className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                <OptionLogo imageUrl={selected.imageUrl} label={selected.label} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate leading-tight">{displayLabel}</span>
                  {selectedHintLines.map((line) => (
                    <span key={line} className="block truncate text-[11px] leading-snug text-muted-foreground/95">
                      {line}
                    </span>
                  ))}
                </span>
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                {selected ? <OptionLogo imageUrl={selected.imageUrl} label={selected.label} /> : null}
                <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform duration-150",
                open && "rotate-180",
              )}
              aria-hidden
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={WORKSPACE_SEARCHABLE_SELECT_PANEL_CLASS}
          align="start"
          side="bottom"
          sideOffset={6}
          collisionPadding={16}
          avoidCollisions
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <Command shouldFilter={false} className="rounded-lg bg-transparent">
            <div className="relative">
              <CommandInput
                value={query}
                onValueChange={(next) => {
                  setQuery(next);
                  onQueryChange?.(next);
                }}
                placeholder={resolvedSearchPlaceholder}
                className="h-8 pr-8 text-xs"
              />
              {query ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1.5 top-1/2 z-10 h-6 w-6 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    resetPopoverQuery();
                  }}
                  aria-label={clearSearchLabel}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
            <CommandList ref={listRef} className="max-h-[min(280px,50vh)] overscroll-contain scroll-py-1 p-1">
              <CommandGroup className="overflow-visible p-0">
                {loading ? (
                  <CommandItem value="_loading" disabled className={WORKSPACE_SEARCHABLE_SELECT_ITEM_CLASS}>
                    <span>{resolvedLoadingMessage}</span>
                  </CommandItem>
                ) : null}
                {showClearRow ? (
                  <CommandItem
                    key="_clear"
                    value="_clear"
                    className={WORKSPACE_SEARCHABLE_SELECT_CLEAR_ITEM_CLASS}
                    onSelect={() => {
                      closePopoverAfterSelect(() => onChange(""), closePopover);
                    }}
                  >
                    <Check className={cn("h-3.5 w-3.5 transition-opacity duration-100", normalizedValue ? "opacity-0" : "opacity-100")} />
                    <span className="italic">{placeholder}</span>
                  </CommandItem>
                ) : null}
                {!loading
                  ? visibleOptions.map((option) => {
                      const optionValue = String(option.value ?? "");
                      const cmdkValue = `${optionValue} ${option.label} ${option.hint ?? ""}`.trim();
                      const isSelected = normalizedValue === optionValue;
                      return (
                        <CommandItem
                          key={optionValue}
                          value={cmdkValue}
                          className={WORKSPACE_SEARCHABLE_SELECT_ITEM_CLASS}
                          data-testid={testId ? `${testId}-option-${optionValue}` : undefined}
                          onSelect={() => {
                            closePopoverAfterSelect(() => onChange(optionValue), closePopover);
                          }}
                        >
                          <Check
                            className={cn(
                              "mt-0.5 h-3.5 w-3.5 transition-opacity duration-100",
                              isSelected ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <OptionItemContent option={option} />
                        </CommandItem>
                      );
                    })
                  : null}
                {hasMore && !loading && onLoadMore !== undefined && (disableClientFilter || !queryNeedle) ? (
                  <CommandItem
                    value="_load_more"
                    onSelect={onLoadMore}
                    className={cn(WORKSPACE_SEARCHABLE_SELECT_ITEM_CLASS, "justify-center font-medium text-muted-foreground")}
                  >
                    {resolvedLoadMoreLabel}
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SearchableSelect;
